import React, { useState, useEffect, useCallback } from 'react';
import { getMerchantInfo, createRefundDetailsSession } from '../services/paymentsService';

const RefundDetailsTab = ({ practiceId }) => {
  const [merchantInfo, setMerchantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [merchantNotFound, setMerchantNotFound] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [refundId, setRefundId] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  const loadMerchantData = useCallback(async () => {
    try {
      setError('');
      const merchantData = await getMerchantInfo(practiceId);
      
      // Check the merchant_status from the API response
      if (merchantData?.merchant_status === 'not_found') {
        setMerchantNotFound(true);
        setMerchantInfo(null);
      } else if (merchantData?.merchant_status === 'found') {
        setMerchantInfo(merchantData?.merchant || null);
        setMerchantNotFound(false);
      } else {
        // Fallback for older API responses that don't have merchant_status
        setMerchantInfo(merchantData?.merchant || null);
        setMerchantNotFound(!merchantData?.merchant);
      }
    } catch (err) {
      setError('Failed to load merchant data. Please check your connection.');
      setMerchantInfo(null);
      setMerchantNotFound(false);
    }
  }, [practiceId]);

  const loadSession = useCallback(async () => {
    try {
      setCreatingSession(true);
      setError('');
      
      // Create a session for the Rainforest refund details component
      const sessionResponse = await createRefundDetailsSession(practiceId);
      setSessionData(sessionResponse);
      
    } catch (err) {
      setError('Failed to create session. Please try again.');
    } finally {
      setCreatingSession(false);
    }
  }, [practiceId]);

  const handleViewRefund = () => {
    if (!refundId || refundId.trim() === '') {
      setError('Please enter a valid Refund ID');
      return;
    }
    setError('');
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await loadMerchantData();
      } catch (err) {
        setError('Failed to load merchant data. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };

    if (practiceId) {
      loadData();
    }
  }, [practiceId, loadMerchantData]);

  // Auto-load session if merchant is found
  useEffect(() => {
    if (merchantInfo && !merchantNotFound && !sessionData && !creatingSession) {
      loadSession();
    }
  }, [merchantInfo, merchantNotFound, sessionData, creatingSession, loadSession]);

  // Add event listeners for the refund details component
  useEffect(() => {
    if (sessionData && showDetails) {
      const comp = document.querySelector('rainforest-refund-details');
      
      const handleCloseButton = () => {
        setShowDetails(false);
      };
      
      const handleViewPayment = (e) => {
        const [payinId] = e.detail;
        console.log('View payment requested:', payinId);
        // Could implement payin details view here
      };
      
      const handleViewAchReturn = (e) => {
        const [achReturnId] = e.detail;
        console.log('View ACH return requested:', achReturnId);
        // Could implement ACH return details view here
      };
      
      if (comp) {
        comp.addEventListener('close-button-clicked', handleCloseButton);
        comp.addEventListener('view-payment', handleViewPayment);
        comp.addEventListener('view-ach-return', handleViewAchReturn);
        
        return () => {
          comp.removeEventListener('close-button-clicked', handleCloseButton);
          comp.removeEventListener('view-payment', handleViewPayment);
          comp.removeEventListener('view-ach-return', handleViewAchReturn);
        };
      }
    }
  }, [sessionData, showDetails]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading refund details...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <p style={{ color: '#b42318', marginBottom: '20px' }}>⚠️ {error}</p>}

      {/* Refund Details Section */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        {merchantInfo && !merchantNotFound ? (
          <div>
            {sessionData && sessionData.session_key ? (
              <div>
                <div style={{ 
                  backgroundColor: '#e6efe9', 
                  padding: '15px', 
                  borderRadius: '4px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#1f5a3d' }}>Refund Details</h4>
                  <p style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: '#3c4540' }}>
                    Enter a Refund ID to view detailed information about a specific refund.
                  </p>
                  
                  {/* Refund ID Input */}
                  {!showDetails && (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="Enter Refund ID (e.g., ref_xxx)"
                        value={refundId}
                        onChange={(e) => setRefundId(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleViewRefund();
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '1px solid #d6dbd7',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      />
                      <button 
                        onClick={handleViewRefund}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#1f5a3d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        🔍 View Details
                      </button>
                    </div>
                  )}
                </div>

                {/* Rainforest Refund Details Component */}
                {showDetails && refundId && (
                  <div>
                    <div style={{ marginBottom: '15px' }}>
                      <button 
                        onClick={handleCloseDetails}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#6b716d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        ← Back to Search
                      </button>
                    </div>
                    
                    <div style={{ 
                      border: '1px solid #e3e7e4', 
                      padding: '20px', 
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      minHeight: '400px'
                    }}>
                      <rainforest-refund-details
                        session-key={sessionData.session_key}
                        refund-id={refundId}
                      ></rainforest-refund-details>
                    </div>
                  </div>
                )}
              </div>
            ) : creatingSession ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                backgroundColor: '#f7f8f7',
                borderRadius: '8px'
              }}>
                <p style={{ fontSize: '1.1rem', color: '#3c4540' }}>Loading session...</p>
              </div>
            ) : (
              <div style={{ 
                backgroundColor: '#fdf3e3', 
                padding: '15px', 
                borderRadius: '4px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#a8520c' }}>
                  ⚠️ Unable to Load Session
                </h4>
                <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#a8520c' }}>
                  There was an issue loading the session. Please try again.
                </p>
                <button 
                  onClick={loadSession}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#1f5a3d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  🔄 Retry
                </button>
              </div>
            )}
          </div>
        ) : merchantNotFound ? (
          <div>
            <div style={{ 
              backgroundColor: '#fdf3e3', 
              padding: '15px', 
              borderRadius: '4px'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#a8520c' }}>
                ⚠️ No Merchant Setup Found
              </h4>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#a8520c' }}>
                This practice doesn't have payment processing setup yet. Please set up your merchant account 
                in the Merchant tab first to view refund details.
              </p>
            </div>
          </div>
        ) : (
          <p style={{ color: '#6b716d', fontStyle: 'italic' }}>
            Loading merchant information...
          </p>
        )}
      </div>
    </div>
  );
};

export default RefundDetailsTab;

